import axios from "axios";
import { unauthorizedInterceptors } from "../utilities/functions";

const pdfParserAPI = axios.create({
  baseURL: `${process.env.REACT_APP_API_BASE_URL}/api/pdf-parser`,
  headers: {
    "Content-Type": "application/json",
  },
});
unauthorizedInterceptors(pdfParserAPI);

const setAuthHeader = () => {
  const token = localStorage.getItem("accessToken");
  if (token) {
    pdfParserAPI.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  }
};

/**
 * Fetch blob from API response - handles both direct blob and S3 presigned URL responses.
 * Always use responseType: "blob" on the request so binary data comes through correctly.
 */
async function fetchBlobFromResponse(response) {
    const blob = response.data;
    if (blob instanceof Blob && (blob.type?.includes("application/json") || blob.size < 2000)) {
        try {
            const text = await blob.text();
            const json = JSON.parse(text);
            const s3Url = json?.data?.download_url || json?.download_url;
            if (s3Url) {
                const s3Response = await fetch(s3Url);
                if (!s3Response.ok) throw new Error(`S3 fetch failed: ${s3Response.status}`);
                return await s3Response.blob();
            }
        } catch { /* not JSON, treat as binary blob */ }
    }
    if (blob instanceof Blob && blob.size > 0) {
        return blob;
    }
    const s3Url = response.data?.data?.download_url || response.data?.download_url;
    if (s3Url) {
        const s3Response = await fetch(s3Url);
        if (!s3Response.ok) throw new Error(`S3 fetch failed: ${s3Response.status}`);
        return await s3Response.blob();
    }
    throw new Error("No download URL or blob in response");
}

// Templates
export const getTemplates = async (brandId = null) => {
  setAuthHeader();
  const params = brandId ? { brand_id: brandId } : {};
  const response = await pdfParserAPI.get("/templates/", { params });
  return { success: true, templates: response.data };
};

export const getTemplate = async (templateId) => {
  setAuthHeader();
  const response = await pdfParserAPI.get(`/templates/${templateId}/`);
  return { success: true, template: response.data };
};

export const uploadTemplate = async (file, name, brandId = null) => {
  setAuthHeader();
  const formData = new FormData();
  formData.append("file", file);
  if (name) formData.append("name", name);
  if (brandId) formData.append("brand_id", brandId);

  const response = await pdfParserAPI.post("/templates/", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
    timeout: 120000, // 2 minutes for large PDFs
  });
  return { success: true, template: response.data };
};

export const updateTemplateMappings = async (templateId, columnMap, fontMap, placeholderSettings) => {
  setAuthHeader();
  const response = await pdfParserAPI.post(
    `/templates/${templateId}/update_mappings/`,
    { column_map: columnMap, font_map: fontMap, placeholder_settings: placeholderSettings, ignore_width: placeholderSettings }
  );
  return { success: true, template: response.data };
};

export const deleteTemplate = async (templateId) => {
  setAuthHeader();
  await pdfParserAPI.delete(`/templates/${templateId}/`);
  return { success: true };
};

export const scanTemplate = async (templateId) => {
  setAuthHeader();
  const response = await pdfParserAPI.get(`/templates/${templateId}/scan/`);
  return { success: true, ...response.data };
};

export const getTemplatePreview = async (templateId) => {
  setAuthHeader();
  const response = await pdfParserAPI.get(`/templates/${templateId}/preview/`, {
    responseType: "blob",
  });
  return await fetchBlobFromResponse(response);
};

export const getTemplateThumbnail = async (templateId) => {
  setAuthHeader();
  const response = await pdfParserAPI.get(`/templates/${templateId}/thumbnail/`, {
    responseType: "blob",
  });
  return response.data;
};

export const getTemplateFile = async (templateId) => {
  setAuthHeader();
  const response = await pdfParserAPI.get(`/templates/${templateId}/file/`, {
    responseType: "blob",
  });
  return await fetchBlobFromResponse(response);
};

export const getTemplateVisualizationData = async (templateId) => {
  setAuthHeader();
  const response = await pdfParserAPI.get(`/templates/${templateId}/visualization/data/`);
  return { success: true, data: response.data };
};

export const getTemplateVisualizationImage = async (templateId, pageNum) => {
  setAuthHeader();
  // Use 'fmt' instead of 'format' to avoid DRF format suffix conflicts
  // Backend accepts both 'format' and 'fmt' for compatibility
  const response = await pdfParserAPI.get(`/templates/${templateId}/visualization/?fmt=png&page=${pageNum}&t=${Date.now()}`, {
    responseType: "blob",
    timeout: 120000, // 2 min — rendering large templates can take 10+ seconds per page
  });
  return response.data;
};

/**
 * Get ALL text positions from PDF for alignment guides
 * @param {number} templateId - Template ID
 * @param {number|null} pageNum - Page number (0-indexed), null for all pages
 * @param {boolean} includePlaceholders - Include placeholder text (default: false)
 * @returns {Promise<{success: boolean, data: {text_blocks: Array, count: number}}>}
 */
export const getTemplateTextPositions = async (templateId, pageNum = null, includePlaceholders = false) => {
  setAuthHeader();
  const params = new URLSearchParams();
  if (pageNum !== null) params.append('page', pageNum);
  if (includePlaceholders) params.append('include_placeholders', 'true');
  
  const response = await pdfParserAPI.get(
    `/templates/${templateId}/visualization/text/?${params.toString()}`
  );
  return response.data;
};

export const editTemplateText = async (templateId, { page, original_text, new_text, bbox }) => {
  setAuthHeader();
  const response = await pdfParserAPI.post(`/templates/${templateId}/edit_text/`, {
    page, original_text, new_text, bbox,
  });
  return response.data;
};

// Data files
export const getDataFiles = async (brandId = null) => {
  setAuthHeader();
  const params = brandId ? { brand_id: brandId } : {};
  const response = await pdfParserAPI.get("/data-files/", { params });
  return { success: true, dataFiles: response.data };
};

export const getDataFile = async (dataFileId) => {
  setAuthHeader();
  const response = await pdfParserAPI.get(`/data-files/${dataFileId}/`);
  return { success: true, dataFile: response.data };
};

export const uploadDataFile = async (file, name, brandId = null) => {
  setAuthHeader();
  const formData = new FormData();
  formData.append("file", file);
  if (name) formData.append("name", name);
  if (brandId) formData.append("brand_id", brandId);

  const response = await pdfParserAPI.post("/data-files/", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
    timeout: 120000, // 2 minutes for large data files
  });
  return { success: true, dataFile: response.data };
};

export const getDataFilePreview = async (dataFileId, rows = 10) => {
  setAuthHeader();
  const response = await pdfParserAPI.get(`/data-files/${dataFileId}/preview/?rows=${rows}`);
  return { success: true, ...response.data };
};

export const autoMapColumns = async (dataFileId, templateId) => {
  setAuthHeader();
  const response = await pdfParserAPI.post(`/data-files/${dataFileId}/auto_map/`, {
    template_id: templateId,
  });
  return { success: true, ...response.data };
};

// Fonts
export const getFonts = async (brandId = null) => {
  setAuthHeader();
  const params = brandId ? { brand_id: brandId } : {};
  const response = await pdfParserAPI.get("/fonts/", { params });
  return { success: true, fonts: response.data };
};

export const uploadFont = async (file, name, fontFamily, brandId = null) => {
  setAuthHeader();
  const formData = new FormData();
  formData.append("file", file);
  if (name) formData.append("name", name);
  if (fontFamily) formData.append("font_family", fontFamily);
  if (brandId) formData.append("brand_id", brandId);

  const response = await pdfParserAPI.post("/fonts/", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
    timeout: 60000, // 1 minute for fonts
  });
  return { success: true, font: response.data, originalName: file.name };
};

export const deleteFont = async (fontId) => {
  setAuthHeader();
  await pdfParserAPI.delete(`/fonts/${fontId}/`);
  return { success: true };
};

export const deleteFontsBulk = async (fontIds) => {
  setAuthHeader();
  const response = await pdfParserAPI.post("/fonts/bulk_delete/", { ids: fontIds });
  return { success: true, deleted: response.data.deleted };
};

export const reprocessFontNames = async () => {
  setAuthHeader();
  const response = await pdfParserAPI.post("/fonts/reprocess_names/");
  return { success: true, ...response.data };
};

export const getFontFile = async (fontId) => {
  setAuthHeader();
  const response = await pdfParserAPI.get(`/fonts/${fontId}/file/`, {
    responseType: "blob",
  });
  return await fetchBlobFromResponse(response);
};

// Generation
export const generateTest = async (options) => {
  setAuthHeader();
  const payload = {
    template_id: options.templateId || options.template_id,
    data_file_id: options.dataPath ? parseInt(options.dataPath, 10) : (options.dataFileId || options.data_file_id || null),
    row_index: options.rowIndex || options.test_row_index || 0,
    column_map: options.columnMap || options.column_map || {},
    font_map: options.fontMap || options.font_map || {},
    placeholder_settings: options.placeholderSettings || options.placeholder_settings || {},
  };
  
  // Log custom regions being sent
  const customRegions = payload.placeholder_settings?._customRegions || [];
  if (customRegions.length > 0) {
    console.log('Sending custom regions to backend:', customRegions.map(r => ({
      id: r.id,
      page: r.page,
      x: r.x,
      y: r.y,
      width: r.width,
      height: r.height,
      content: r.content?.substring(0, 30),
    })));
  }
  
  const response = await pdfParserAPI.post("/test-generate/", payload, {
    responseType: "blob",
  });
  return response.data;
};

// Alias for backwards compatibility
export const testGenerate = generateTest;

export const generateBatch = async (options) => {
  setAuthHeader();
  const response = await pdfParserAPI.post("/generations/", {
    template_id: options.templateId || options.template_id,
    data_file_id: options.dataPath ? parseInt(options.dataPath, 10) : (options.dataFileId || options.data_file_id || null),
    column_map: options.columnMap || options.column_map || {},
    font_map: options.fontMap || options.font_map || {},
    placeholder_settings: options.placeholderSettings || options.placeholder_settings || {},
    id_column: options.idColumn || options.id_column || 'person_id',
    combine: options.combine || false,
    test_rows: options.testRows || options.test_rows,
    brand_id: options.brandId || options.brand_id || null,
  });
  // Backend returns {generation: {...}, status: 'processing'}
  const generation = response.data.generation || response.data;
  return { 
    success: true, 
    generation: generation, 
    generationId: generation.id, 
    batchId: generation.batch_id, 
    total_rows: generation.total_rows,
    status: response.data.status || 'processing'
  };
};

export const cleanupStuckGenerations = async (hours = 1) => {
  setAuthHeader();
  const response = await pdfParserAPI.post("/generations/cleanup_stuck/", { hours });
  return { success: true, ...response.data };
};

export const getGenerations = async (brandId = null) => {
  setAuthHeader();
  try {
    const params = brandId ? { brand_id: brandId } : {};
    const response = await pdfParserAPI.get("/generations/", { params });
    // Backend may return array directly or wrapped in object
    const generations = Array.isArray(response.data) ? response.data : (response.data.results || response.data.generations || []);
    return { success: true, generations };
  } catch (err) {
    console.error("Failed to fetch generations:", err);
    throw err;
  }
};

export const getGeneration = async (generationId) => {
  setAuthHeader();
  const response = await pdfParserAPI.get(`/generations/${generationId}/`);
  return { success: true, generation: response.data };
};

// Alias for getGeneration
export const getGenerationDetails = getGeneration;

export const getGenerationFiles = async (generationId) => {
  setAuthHeader();
  try {
    const response = await pdfParserAPI.get(`/generations/${generationId}/files/`);
    // Backend returns {files: [...]}
    const files = response.data.files || (Array.isArray(response.data) ? response.data : []);
    return { success: true, files };
  } catch (err) {
    console.error("Failed to fetch generation files:", err);
    return { success: false, files: [], error: err.message };
  }
};

// Alias for getGenerationFiles
export const getGeneratedFiles = getGenerationFiles;

async function streamDownload(path, params = {}, returnBlob = false) {
  const baseURL = process.env.REACT_APP_API_BASE_URL || "";
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v != null) qs.set(k, v);
  }
  const queryStr = qs.toString() ? "?" + qs.toString() : "";

  try {
    const fullUrl = `${baseURL}/api/pdf-parser${path}${queryStr}`;
    const token = localStorage.getItem("accessToken");
    const headers = {};
    if (token) headers["Authorization"] = `Bearer ${token}`;

    const response = await fetch(fullUrl, { headers, signal: AbortSignal.timeout(300000) });
    if (!response.ok) {
      let errMsg = `Server error ${response.status}`;
      try {
        const text = await response.text();
        if (text.trimStart().startsWith("{")) {
          const data = JSON.parse(text);
          errMsg = data.error || data.message || data.detail || errMsg;
        }
      } catch { /* use default */ }
      throw new Error(errMsg);
    }
    const ct = response.headers.get("content-type") || "";
    if (ct.includes("application/json")) {
      const data = await response.json();
      const s3Url = data?.data?.download_url || data?.download_url;
      if (s3Url) {
        if (returnBlob) {
          const s3Response = await fetch(s3Url);
          if (!s3Response.ok) throw new Error(`S3 download failed: ${s3Response.status}`);
          return await s3Response.blob();
        }
        // INSTANT DOWNLOAD: browser downloads directly from S3
        const iframe = document.createElement("iframe");
        iframe.style.display = "none";
        iframe.src = s3Url;
        document.body.appendChild(iframe);
        setTimeout(() => document.body.removeChild(iframe), 60000);
        return null;
      }
      throw new Error(data?.error || data?.message || "Unexpected response");
    }
    const blob = await response.blob();
    if (!blob || blob.size === 0) throw new Error("Empty response from server");
    return blob;
  } catch (fetchErr) {
    if (fetchErr.message && !fetchErr.message.startsWith("Server error")) {
      console.warn("[streamDownload] fetch failed, falling back to axios:", fetchErr.message);
      setAuthHeader();
      const response = await pdfParserAPI.get(path, {
        params,
        responseType: "blob",
        timeout: 300000,
      });
      if (response.status >= 400) {
        throw new Error(`Server error ${response.status}`);
      }
      const blob = response.data;
      if (!blob || blob.size === 0) throw new Error("Empty response from server");
      return blob;
    }
    throw fetchErr;
  }
}

export const downloadCombinedPdf = async (generationId, preview = false) => {
  const params = { download: preview ? 'inline' : 'attachment' };
  return streamDownload(`/generations/${generationId}/download_combined/`, params, preview);
};

export const downloadGeneratedFile = async (fileId) => {
  return streamDownload(`/generated-files/${fileId}/download/`, { download: "attachment" });
};

export const downloadGenerationZip = async (generationId) => {
  return streamDownload(`/generations/${generationId}/download_zip/`);
};

export const deleteGeneration = async (generationId) => {
  setAuthHeader();
  try {
    await pdfParserAPI.delete(`/generations/${generationId}/`);
    return { success: true };
  } catch (err) {
    const msg = err.response?.data?.error || err.response?.data?.detail || err.message || "Failed to delete generation";
    return { success: false, message: msg };
  }
};

export const rebuildGenerationZip = async (generationId) => {
  setAuthHeader();
  try {
    const response = await pdfParserAPI.post(`/generations/${generationId}/rebuild_zip/`);
    return response.data;
  } catch (err) {
    console.error("Failed to rebuild ZIP:", err);
    throw err;
  }
};

export const rebuildCombinedPdf = async (generationId) => {
  setAuthHeader();
  try {
    const response = await pdfParserAPI.post(`/generations/${generationId}/rebuild_combined/`);
    return response.data;
  } catch (err) {
    console.error("Failed to rebuild combined PDF:", err);
    throw err;
  }
};

// Helper functions for URLs (with auth header)
export const getAuthHeaders = () => {
  const token = localStorage.getItem("accessToken");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default {
  getTemplates,
  getTemplate,
  uploadTemplate,
  updateTemplateMappings,
  deleteTemplate,
  scanTemplate,
  getTemplatePreview,
  getTemplateThumbnail,
  getTemplateFile,
  getTemplateVisualizationData,
  getTemplateVisualizationImage,
  getTemplateTextPositions,
  editTemplateText,
  getDataFiles,
  getDataFile,
  uploadDataFile,
  getDataFilePreview,
  autoMapColumns,
  getFonts,
  uploadFont,
  deleteFont,
  testGenerate,
  generateTest,
  generateBatch,
  getGenerations,
  getGeneration,
  getGenerationDetails,
  getGenerationFiles,
  getGeneratedFiles,
  downloadCombinedPdf,
  downloadGeneratedFile,
  downloadGenerationZip,
  rebuildGenerationZip,
  rebuildCombinedPdf,
  deleteGeneration,
  getAuthHeaders,
};
